import type { LocalDate } from '@cadencia/contracts';
import { MUSCLE_LABELS } from '../catalog/muscles.js';
import { daysBetweenLocal } from '../units/local-date.js';
import type { OutcomeVerdict } from './outcome.js';

/**
 * Lo que se ha aprendido de ESTE atleta (§10 del encargo, E5 del diagnóstico).
 *
 * POR QUÉ SE DERIVA Y NO SE GUARDA
 *
 * El §10 pide «almacenar observaciones e hipótesis revisables». Revisables es la
 * palabra que decide el diseño: una hipótesis guardada en una fila se queda
 * como estaba aunque los datos que la sostenían cambien —una decisión
 * reevaluada, una sesión borrada, un bloque nuevo— y eso es exactamente una
 * conclusión que ya no es verdad y sigue pareciéndolo. Derivada de los
 * resultados cada vez que se pide, no puede discrepar de los datos, porque ES
 * los datos.
 *
 * `user_preferences.learned_preferences` existe desde la Fase 1 para algo
 * parecido y nunca lo ha escrito nadie. No se reutiliza, y la razón está en su
 * forma: lleva un `confidence` numérico de 0 a 1, y el §10 advierte contra eso
 * —«no confundas una puntuación generada por el modelo con una probabilidad
 * calibrada»—. Un 0,73 aquí se leería como un 73 % de acierto, que no es.
 *
 * LO QUE ESTO NO HACE, y conviene decirlo antes de nada
 *
 * * No descubre un volumen óptimo ni una frecuencia ideal. «No afirmes haber
 *   descubierto un volumen óptimo, una frecuencia ideal o una relación causal a
 *   partir de unos pocos registros» (§10). Describe lo que pasó en los episodios
 *   observados, con cuántos fueron.
 * * No atribuye causas. Hereda de `outcome.ts` el «asociado a».
 * * No sube de `moderate` por muchos datos que haya. Ver `MEMORY_RULES`.
 */

/**
 * Cuándo una observación deja de ser anécdota. CONVENCIÓN DECLARADA.
 *
 * Los tres niveles son CUALITATIVOS a propósito, no un número: el §10 pide
 * «confianza conforme a criterios documentados» y prohíbe presentar una
 * puntuación como probabilidad. Estos son los criterios, documentados.
 */
export const MEMORY_RULES = {
  /**
   * Episodios CONCLUYENTES a partir de los cuales la observación es `weak`.
   * Con cero, `insufficient`: no se sabe nada, y se dice.
   */
  weakFrom: 1,
  /**
   * A partir de cuántos concluyentes, y todos en el mismo sentido, es
   * `moderate`.
   */
  moderateFrom: 3,
  /**
   * `moderate` es el TECHO. Con los datos de una sola persona no hay manera de
   * controlar lo que no se mide —sueño, estrés, comida, vida—, así que ninguna
   * cantidad de episodios propios convierte una asociación en algo firme. Un
   * nivel `high` aquí sería la promesa que el §10 prohíbe hacer.
   */
  ceiling: 'moderate' as const,
  /**
   * Antigüedad máxima de un episodio para que cuente, en días. Un año: una
   * respuesta al volumen de hace tres años describe a otra persona, con otra
   * edad de entrenamiento.
   */
  maxAgeDays: 365,
} as const;

export type MemoryConfidence = 'insufficient' | 'weak' | 'moderate';

/** Un cambio de volumen aplicado y ya evaluado. */
export interface VolumeEpisode {
  muscle: string;
  /** Positivo si se subió el volumen, negativo si se bajó. */
  deltaSets: number;
  verdict: OutcomeVerdict;
  /** Cuándo se evaluó. */
  evaluatedOn: LocalDate;
}

export interface VolumeObservation {
  muscle: string;
  direction: 'increase' | 'decrease';
  /** Episodios que cuentan, concluyentes o no. */
  episodes: number;
  /** Los que dieron un veredicto: el resto fue `inconclusive`. */
  conclusive: number;
  improved: number;
  declined: number;
  unchanged: number;
  confidence: MemoryConfidence;
  /** La frase, con el lenguaje proporcionado que exige el §10. */
  statement: string;
  firstSeen: LocalDate;
  lastSeen: LocalDate;
}

function nivel(conclusive: number, todosEnElMismoSentido: boolean): MemoryConfidence {
  if (conclusive < MEMORY_RULES.weakFrom) return 'insufficient';
  if (conclusive >= MEMORY_RULES.moderateFrom && todosEnElMismoSentido) return MEMORY_RULES.ceiling;
  return 'weak';
}

const VERBO: Record<'increase' | 'decrease', string> = {
  increase: 'Subir el volumen',
  decrease: 'Bajar el volumen',
};

function frase(o: Omit<VolumeObservation, 'statement'>): string {
  // El nombre que ve el usuario, no el identificador: la frase se pinta tal
  // cual en Progreso y viaja tal cual a la IA.
  const nombre = (MUSCLE_LABELS as Record<string, string>)[o.muscle]?.toLowerCase() ?? o.muscle;
  const cabeza = `${VERBO[o.direction]} de ${nombre}`;
  if (o.conclusive === 0) {
    return `${cabeza}: evidencia insuficiente (${String(o.episodes)} episodio(s), ninguno concluyente).`;
  }
  const partes: string[] = [];
  if (o.improved > 0) partes.push(`${String(o.improved)} asociado(s) a mejor rendimiento`);
  if (o.unchanged > 0) partes.push(`${String(o.unchanged)} sin cambio claro`);
  if (o.declined > 0) partes.push(`${String(o.declined)} asociado(s) a peor rendimiento`);
  return `${cabeza}: de ${String(o.conclusive)} episodio(s) concluyente(s) en los bloques observados, ${partes.join(', ')}.`;
}

/**
 * Observaciones sobre cómo responde cada músculo a subir o bajar volumen.
 *
 * Se agrupan por músculo Y DIRECCIÓN. Mezclar subidas y bajadas en un solo
 * recuento contaría como «tolera el volumen» a un músculo que mejora cuando se
 * le quita, que es justo lo contrario.
 *
 * `hoy` entra por parámetro: el dominio no mira el reloj.
 */
export function volumeObservations(
  episodes: readonly VolumeEpisode[],
  hoy: LocalDate,
): VolumeObservation[] {
  const vigentes = episodes.filter(
    (e) => e.deltaSets !== 0 && daysBetweenLocal(e.evaluatedOn, hoy) <= MEMORY_RULES.maxAgeDays,
  );

  const grupos = new Map<string, VolumeEpisode[]>();
  for (const e of vigentes) {
    const clave = `${e.muscle}|${e.deltaSets > 0 ? 'increase' : 'decrease'}`;
    grupos.set(clave, [...(grupos.get(clave) ?? []), e]);
  }

  const salida: VolumeObservation[] = [];
  for (const [clave, lista] of grupos) {
    const [muscle, dir] = clave.split('|') as [string, 'increase' | 'decrease'];
    const improved = lista.filter((e) => e.verdict === 'associated_improvement').length;
    const declined = lista.filter((e) => e.verdict === 'associated_decline').length;
    const unchanged = lista.filter((e) => e.verdict === 'no_clear_change').length;
    const conclusive = improved + declined + unchanged;
    const fechas = lista.map((e) => e.evaluatedOn).sort();

    // «Todos en el mismo sentido» es lo que distingue una respuesta que se
    // repite de una que sale una vez de cada manera. Tres episodios que dicen
    // tres cosas distintas no son una observación moderada: son ruido.
    const mismoSentido =
      conclusive > 0 && (improved === conclusive || declined === conclusive || unchanged === conclusive);

    const base = {
      muscle,
      direction: dir,
      episodes: lista.length,
      conclusive,
      improved,
      declined,
      unchanged,
      confidence: nivel(conclusive, mismoSentido),
      firstSeen: fechas[0] as LocalDate,
      lastSeen: fechas[fechas.length - 1] as LocalDate,
    };
    salida.push({ ...base, statement: frase(base) });
  }

  // Primero lo más firme: es lo que más información aporta a quien programa.
  const peso: Record<MemoryConfidence, number> = { moderate: 0, weak: 1, insufficient: 2 };
  return salida.sort(
    (a, b) => peso[a.confidence] - peso[b.confidence] || a.muscle.localeCompare(b.muscle),
  );
}

/**
 * Lo que la IA recibe de la memoria al diseñar el siguiente bloque.
 *
 * Solo lo que tiene al menos `weak`: una observación `insufficient` no aporta
 * nada que diseñar, y mandarla gastaría contexto —que tiene tope, CA-7.6— en
 * decir «no se sabe». Y solo la frase y el nivel: el recuento ya está dentro de
 * la frase, y el §14 pide transmitir «únicamente los datos deportivos
 * necesarios».
 */
export function memoryForPlanning(
  observations: readonly VolumeObservation[],
  max = 6,
): { finding: string; confidence: MemoryConfidence }[] {
  return observations
    .filter((o) => o.confidence !== 'insufficient')
    .slice(0, max)
    .map((o) => ({ finding: o.statement, confidence: o.confidence }));
}
